/**
 * RIS Viewer Context
 *
 * Global state management for the in-app RIS document viewer.
 * Provides methods to open/close the viewer and manages document state.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { buildApiUrl } from '../config/api';
import { ViewerRequestGuard } from '../services/risViewerLinks';
import { useAuth } from './AuthContext';
import { viewerRequest, viewerTarget, type ViewerArt } from '../services/viewerRequest';

// ===== TYPES =====

export interface Highlight {
  farbe: 'gelb' | 'orange';
  anfang: string;
  ende: string;
  grund?: string;
}

export interface HighlightResult {
  highlights: Highlight[];
  processingTimeMs?: number;
  fromCache?: boolean;
}

export interface DocumentMetadata {
  title: string;
  gericht: string;
  geschaeftszahl: string;
  datum: string;
  dokumentTyp?: 'RS' | 'TE' | 'KV' | 'PDF' | 'DOCX' | 'DOC';
  ecli?: string;
  sourceUrl?: string | null;
  gesetzesnummer?: string;
  dokumentnummer?: string;
  imRisSeit?: string;
  eli?: string;
  entscheidungsart?: string;
  rechtsgebiet?: string;
  rechtssatznummer?: string;
  /** Einzeiliger Hinweis in der Kopfzeile, z. B. "Ältere Fassung". */
  hinweis?: string;
}

/**
 * Eine relevante Passage als Klartext-Fenster des gespeicherten Originals.
 * Wird für die Hervorhebung in der PDF-Vorschau verwendet.
 */
export interface ViewerPassage {
  text: string;
  chunkIndex: number;
  sectionTitle: string | null;
  relevanz: number;
  farbe: 'gelb' | 'orange';
}

export interface ViewerData {
  html: string;
  originalHtml?: string;
  metadata: DocumentMetadata;
  highlights: HighlightResult;
  insertedHighlightCount?: number; // Actual number of visible highlights
  fromCache: boolean;
  pdfDownloadUrl?: string | null;
  /** Relevante Passagen des gespeicherten Originals; fehlt bei älteren Antworten. */
  passages?: ViewerPassage[];
  /** Pfad zum gespeicherten Original, sofern es ein PDF ist. */
  pdfPreviewUrl?: string | null;
}

export interface RISViewerState {
  isOpen: boolean;
  isLoading: boolean;
  error: string | null;
  url: string | null;
  query: string;
  context: string;
  data: ViewerData | null;
}

export interface RISViewerContextType {
  state: RISViewerState;
  openViewer: (url: string, query: string, context?: string) => Promise<void>;
  openDocumentViewer: (documentId: string, query: string, context?: string) => Promise<void>;
  closeViewer: () => void;
  refreshViewer: () => Promise<void>;
}

// ===== CONTEXT =====

const RISViewerContext = createContext<RISViewerContextType | undefined>(undefined);

// ===== PROVIDER =====

interface RISViewerProviderProps {
  children: ReactNode;
}

export function viewerPostRequestOptions(
  body: { url: string; query: string; context?: string } | { documentId: string; query: string; context?: string },
  signal: AbortSignal,
): RequestInit {
  return {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  };
}

export const RISViewerProvider: React.FC<RISViewerProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const requestGuardRef = useRef(new ViewerRequestGuard());
  const identityRef = useRef<number | null | undefined>(undefined);
  const [state, setState] = useState<RISViewerState>({
    isOpen: false,
    isLoading: false,
    error: null,
    url: null,
    query: '',
    context: '',
    data: null
  });

  /**
   * Fetch a document from the viewer API. Question and answer travel in the JSON body of a
   * POST (viewerRequest), never in the URL, so they cannot end up in an access log (B-2).
   */
  const fetchViewerDocument = async (art: ViewerArt, ziel: string, query: string, context: string | undefined, signal: AbortSignal): Promise<ViewerData> => {
    const { path, init } = viewerRequest(art, ziel, query, context);
    // Cookies are sent automatically with credentials: 'include'
    const response = await fetch(buildApiUrl(path), { ...init, signal });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error ${response.status}`);
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'Unknown error');
    }

    return result.data;
  };

  /**
   * Open the viewer with a RIS URL
   */
  const openViewer = useCallback(async (url: string, query: string, context?: string) => {
    console.log(`[RISViewerContext] Opening viewer for: ${url.substring(0, 60)}...`);

    const request = requestGuardRef.current.begin();
    setState(prev => ({
      ...prev,
      isOpen: true,
      isLoading: true,
      error: null,
      url,
      query,
      context: context || '',
      data: null
    }));

    try {
      const data = await fetchViewerDocument('ris', url, query, context, request.controller.signal);
      if (!requestGuardRef.current.isCurrent(request)) return;

      setState(prev => ({
        ...prev,
        isLoading: false,
        data
      }));

      console.log(`[RISViewerContext] Document loaded: ${data.highlights.highlights.length} highlights`);
    } catch (error) {
      if (!requestGuardRef.current.isCurrent(request)) return;
      console.error('[RISViewerContext] Error loading document:', error);

      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load document'
      }));
    }
  }, []);

  const openDocumentViewer = useCallback(async (documentId: string, query: string, context?: string) => {
    console.log(`[RISViewerContext] Opening document viewer for: ${documentId}`);

    const request = requestGuardRef.current.begin();
    setState(prev => ({
      ...prev,
      isOpen: true,
      isLoading: true,
      error: null,
      url: `document://${documentId}`,
      query,
      context: context || '',
      data: null
    }));

    try {
      const data = await fetchViewerDocument('rag', documentId, query, context, request.controller.signal);
      if (!requestGuardRef.current.isCurrent(request)) return;

      setState(prev => ({
        ...prev,
        isLoading: false,
        data
      }));

      console.log(`[RISViewerContext] Document loaded: ${data.highlights.highlights.length} highlights`);
    } catch (error) {
      if (!requestGuardRef.current.isCurrent(request)) return;
      console.error('[RISViewerContext] Error loading document:', error);

      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load document'
      }));
    }
  }, []);

  /**
   * Close the viewer
   */
  const closeViewer = useCallback(() => {
    console.log('[RISViewerContext] Closing viewer');
    requestGuardRef.current.cancel();

    setState({
      isOpen: false,
      isLoading: false,
      error: null,
      url: null,
      query: '',
      context: '',
      data: null,
    });
  }, []);

  useEffect(() => () => requestGuardRef.current.cancel(), []);

  useEffect(() => {
    const identity = user?.id ?? null;
    if (identityRef.current !== undefined && identityRef.current !== identity) {
      requestGuardRef.current.cancel();
      setState({
        isOpen: false,
        isLoading: false,
        error: null,
        url: null,
        query: '',
        context: '',
        data: null,
      });
    }
    identityRef.current = identity;
  }, [user?.id]);

  /**
   * Refresh the current document (useful to regenerate highlights)
   */
  const refreshViewer = useCallback(async () => {
    if (!state.url || !state.query) {
      console.warn('[RISViewerContext] Cannot refresh: no URL or query');
      return;
    }

    console.log('[RISViewerContext] Refreshing viewer');

    const request = requestGuardRef.current.begin();
    setState(prev => ({
      ...prev,
      isLoading: true,
      error: null
    }));

    try {
      // RAG documents are held as document://<id> and need the RAG route (N-2).
      const { art, ziel } = viewerTarget(state.url);
      const data = await fetchViewerDocument(art, ziel, state.query, state.context, request.controller.signal);
      if (!requestGuardRef.current.isCurrent(request)) return;

      setState(prev => ({
        ...prev,
        isLoading: false,
        data
      }));
    } catch (error) {
      if (!requestGuardRef.current.isCurrent(request)) return;
      console.error('[RISViewerContext] Error refreshing document:', error);

      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to refresh document'
      }));
    }
  }, [state.url, state.query, state.context]);

  const value: RISViewerContextType = {
    state,
    openViewer,
    openDocumentViewer,
    closeViewer,
    refreshViewer
  };

  return (
    <RISViewerContext.Provider value={value}>
      {children}
    </RISViewerContext.Provider>
  );
};

// ===== HOOK =====

export const useRISViewer = (): RISViewerContextType => {
  const context = useContext(RISViewerContext);

  if (context === undefined) {
    throw new Error('useRISViewer must be used within a RISViewerProvider');
  }

  return context;
};

export default RISViewerContext;
